import React, { useState, createContext, useContext } from 'react';
import { GRID_SPACING_DEFAULT } from './constants';
import { useApplicationContext } from '@/context/v1/Application/context';
import type { Context, FilterFields } from './types';

interface DataExtractionProviderProps {
	children: JSX.Element;
}

const DataExtractionContext = createContext<Context>({} as Context);

export function DataExtractionProvider({ children }: Readonly<DataExtractionProviderProps>) {
	const { organization, company } = useApplicationContext();

	const defaultFilter: FilterFields = {
		organization_id: organization?.id!,
		company_id: company?.id!,
		page: 1,
		limit: 10
	};

	const [filter, setFilter] = useState<FilterFields>(defaultFilter);

	function handleOnSetFilter(values: FilterFields): void {
		setFilter((prevState) => ({ ...prevState, ...values }));
	}

	function handleOnSetPage(page: number): void {
		setFilter((prevState) => ({ ...prevState, page }));
	}

	function handleOnSetLimit(limit: number): void {
		setFilter((prevState) => ({ ...prevState, limit }));
	}

	function handleOnClearFilter(): void {
		setFilter(defaultFilter);
	}

	const contextValues: Context = {
		filter,
		gutterLayout: GRID_SPACING_DEFAULT,
		handleOnSetFilter,
		handleOnSetPage,
		handleOnSetLimit,
		handleOnClearFilter
	};

	return <DataExtractionContext.Provider value={contextValues}>{children}</DataExtractionContext.Provider>;
}

export const useDataExtractionContext = () => {
	const context = useContext(DataExtractionContext);

	if (!context) {
		throw new Error('useDataExtractionContext must be used within a DataExtractionProvider');
	}

	return context;
};
